import { FormatNumberOptions, NumberStyleEnum } from '@monetize/types/common';

// NOTE: Intl.NumberFormat should handle any formatting we want, so consider adding the appropriate option to this function
// before building our own strings (ie. setting currencySign = 'accounting' gives us parentheses instead of '-' to indicate negative number)
// For details on Intl.NumberFormat options (ie. currencySign), see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat

interface NumberFormatterOptions {
  locale: string;
  style: NumberStyleEnum;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  currency: string | undefined;
  currencySign: 'standard' | 'accounting' | undefined;
  signDisplay: 'never' | 'auto';
}

const numberFormatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Returns a cached `Intl.NumberFormat` for the given options.
 *
 * Constructing a formatter is comparatively expensive and table cells format
 * one value per cell per render, so the same handful of formatters were being
 * rebuilt constantly.
 *
 * Callers must pass *normalized* options - the fraction-digit clamping has to
 * happen first, or two option sets that resolve to the same formatter would get
 * separate cache entries.
 */
export function getNumberFormatter(
  options: NumberFormatterOptions,
): Intl.NumberFormat {
  const cacheKey = [
    options.locale,
    options.style,
    options.minimumFractionDigits,
    options.maximumFractionDigits,
    options.currency ?? '',
    options.currencySign ?? '',
    options.signDisplay,
  ].join('|');

  const cached = numberFormatterCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const formatter = new Intl.NumberFormat(options.locale, {
    style: options.style,
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
    currency: options.currency,
    currencySign: options.currencySign,
    signDisplay: options.signDisplay,
  });
  numberFormatterCache.set(cacheKey, formatter);
  return formatter;
}

export const formatNumber = (
  input: number | string | null | undefined,
  {
    style = NumberStyleEnum.DECIMAL,
    zeroStr,
    currency,
    currencySign,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-US',
  }: FormatNumberOptions = {},
): string => {
  let value;
  if (input || input === 0) {
    value = +input;
  } else {
    return '';
  }
  if (zeroStr && value === 0) {
    return zeroStr;
  }

  if (maximumFractionDigits === 'max' || maximumFractionDigits > 16) {
    maximumFractionDigits = 16; // max supported by JS is documented as 20 but in practice seems to be 16
  }
  if (minimumFractionDigits > maximumFractionDigits) {
    minimumFractionDigits = maximumFractionDigits;
  }

  const formatter = getNumberFormatter({
    locale,
    style,
    minimumFractionDigits,
    maximumFractionDigits,
    currency,
    currencySign,
    // Varies with the value, so it is part of the cache key.
    signDisplay: value === 0 ? 'never' : 'auto',
  });
  return formatter.format(value);
};

export const formatInteger = (
  input: number | string | null | undefined,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    ...rest
  }: FormatNumberOptions = {},
): string => {
  return formatNumber(input, {
    minimumFractionDigits,
    maximumFractionDigits,
    ...rest,
  });
};

// ISO 4217 minor units, matching billing-platform's Currency enum. Intl's own
// defaults are not used: they give 0 for HUF and IDR, which the backend keeps at 2.
const ZERO_DECIMAL_CURRENCIES = new Set(['CLP', 'ISK', 'JPY']);

/** Number of decimal places money amounts in this currency are rounded to. */
export const getCurrencyDecimals = (currency: string | null | undefined) =>
  currency && ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2;

const currencySymbolCache = new Map<string, string>();

/**
 * Get the short currency symbol used as a money input prefix, from Intl
 * (en-US, `narrowSymbol`). Narrow so it fits the prefix slot: CAD is `$`
 * here, while `formatCurrency` renders amounts as `CA$`.
 * Falls back to the currency code if Intl does not know the currency.
 */
export const getCurrencySymbol = (currency: string): string => {
  const cached = currencySymbolCache.get(currency);
  if (cached) {
    return cached;
  }
  let symbol = currency;
  try {
    symbol =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find(({ type }) => type === 'currency')?.value ?? currency;
  } catch {
    // not a valid ISO 4217 code
  }
  currencySymbolCache.set(currency, symbol);
  return symbol;
};

export const formatCurrency = (
  input: number | string | null | undefined,
  {
    style = NumberStyleEnum.CURRENCY,
    currency = 'USD',
    currencySign = 'accounting', // parentheses for negative number
    minimumFractionDigits,
    maximumFractionDigits,
    ...rest
  }: // }: Omit<FormatNumberOptions, 'currency'> & { currency: string },
  FormatNumberOptions = {},
): string => {
  return formatNumber(input, {
    style,
    currency,
    currencySign,
    // `??` rather than destructuring defaults, so callers that forward an
    // explicit `undefined` still get the currency's own precision
    minimumFractionDigits:
      minimumFractionDigits ?? getCurrencyDecimals(currency),
    maximumFractionDigits: maximumFractionDigits ?? 'max',
    ...rest,
  });
};

// for signed incremental/delta values (e.g. "+$3,779.40", "-$1,258.80") where the
// sign itself is meaningful, unlike formatCurrency's default accounting/parentheses style
export const formatCurrencyDelta = (
  input: number | string | null | undefined,
  { currencySign = 'standard', ...rest }: FormatNumberOptions = {},
): string => {
  const value = input === null || input === undefined ? 0 : +input;
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatCurrency(value, { currencySign, ...rest })}`;
};

// for use with real number values, not values that are the numerator of a percent
// ie. the value .0918, which would be displayed as 9.18%
export const formatPercent = (
  input: number | string | null | undefined,
  { style = NumberStyleEnum.PERCENT, ...rest }: FormatNumberOptions = {},
): string => {
  return formatNumber(input, { style, ...rest });
};

// handles a value that is expressed as the numerator only and needs to be divided by denominator 100 to find the true value
// ie. the value 9.18 which is intended to mean 9.18/100 and would be displayed as 9.18%
export const formatPercentNumerator = (
  input: number | string | null | undefined,
  style: FormatNumberOptions = {},
): string => {
  return formatPercent(input ? Number(input) / 100 : input, style);
};

export const roundNumberToDecimal = (value: number, digits = 2) => {
  let negative = false;
  if (digits === undefined) {
    digits = 0;
  }
  if (value < 0) {
    negative = true;
    value = value * -1;
  }
  const multiplicator = Math.pow(10, digits);
  value = parseFloat((value * multiplicator).toFixed(11));
  value = +(Math.round(value) / multiplicator).toFixed(digits);
  if (negative) {
    value = +(value * -1).toFixed(digits);
  }
  return value;
};
